import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import bindMethods from 'auto-bind';

import { getQlikAppId } from '../state/selectors/getQlikAppId';
import { getQlikSheetId } from '../state/selectors/getQlikSheetId';
import {
  getPrintDownAndDistanceValues,
  getPrintFieldZoneValues,
  getPrintBreadcrumbsValues,
  getPrintGameValues,
} from '../state/selectors/print';
import { setGridObjects } from '../state/grid';
import { getUniqueDownDistanceValues, getUniqueFieldZoneValues } from '../utils/filters';
import { getQlikApp } from '../utils/qlik';
import { FOOTBALL_DOWN_DISTANCE, FOOTBALL_FIELD_ZONE } from '../constants/fields';
import ApplicationSpinner from '../layouts/ApplicationSpinner';

const QlikGridContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
  padding: 20px;
  display: grid;
  grid-template-columns: repeat(24, 1fr);
  grid-template-rows: repeat(12, 1fr);
`;
const QlikApp = styled.div`
  height: 100%;
  width: 100%;
  display: block;
  padding-bottom: 50px;
`;

const QlikGridObject = styled.div`
  position: relative;
  grid-column-start: ${props => props.columnStart};
  grid-column-end: ${props => props.columnEnd};
  grid-row-start: ${props => props.rowStart};
  grid-row-end: ${props => props.rowEnd};
`;

const PrintInformation = styled.div`
  position: relative;
  text-align: left;
  margin-bottom: 20px;
`;

const PageName = styled.h1`
  font-size: 30px;
  font-weight: 700;
  padding-bottom: 15px;
`;

const SidebarSelections = styled.div`
  font-size: 18px;
  padding: 5px 0px;
  font-weight: 500;
`;

class QlikGrid extends PureComponent {
  constructor(props) {
    super(props);
    bindMethods(this);
    this.app = null;
    this.state = {
      isQlikDataLoaded: false,
    };
  }

  async componentDidMount() {
    const { qlikAppId } = this.props;
    this.app = await getQlikApp(qlikAppId);
    this.getQlikSheetObjects();
  }

  componentDidUpdate(prevProps) {
    const { qlikSheetId, ddSelections, fzSelections } = this.props;
    const isDD = Object.keys(prevProps.ddSelections).length !== Object.keys(ddSelections).length;
    const isFZ = Object.keys(prevProps.fzSelections).length !== Object.keys(fzSelections).length;

    if (prevProps.qlikSheetId !== qlikSheetId) {
      this.getQlikSheetObjects();
    }

    if (isDD || isFZ) {
      this.handleCustomSelections(isDD, isFZ);
    }
  }

  async getQlikSheetObjects() {
    const { qlikAppId, qlikSheetId, setGridObjects } = this.props;

    if (qlikAppId && qlikSheetId) {
      const sheets = await this.app.getList('sheet');

      const sheet = sheets.layout.qAppObjectList.qItems.find(s => s.qInfo.qId === qlikSheetId);

      const objects = sheet.qData.cells.map(c => ({
        ...c,
        col: c.col === 0 ? 1 : c.col + 1,
        colspan: c.colspan,
        row: c.row === 0 ? 1 : c.row + 1,
        rowspan: c.rowspan,
      }));

      setGridObjects(objects);
      this.renderQlikObjects();
    }
  }

  handleCustomSelections(dd, fz) {
    const {
      ddSelections, ddPossibleSelections, fzSelections, fzPossibleSelections,
    } = this.props;
    let values;
    switch (dd || fz) {
      case dd:
        values = getUniqueDownDistanceValues(ddSelections, ddPossibleSelections);
        return this.app.field(FOOTBALL_DOWN_DISTANCE).selectValues(values, false, false);
      case fz:
        values = getUniqueFieldZoneValues(fzSelections, fzPossibleSelections);
        return this.app.field(FOOTBALL_FIELD_ZONE).selectValues(values, false, false);
      default:
        return null;
    }
  }

  async renderQlikObjects() {
    const { objects } = this.props;
    await Promise.all(
      objects.map(async (object) => {
        await this.app.getObject(object.name, object.name);
      }),
    ).then(() => {
      this.setState({
        isQlikDataLoaded: true,
      });
    });
  }

  renderQlikObjectContainers() {
    const { objects } = this.props;

    return objects.map(object => (
      <QlikGridObject
        key={object.name}
        id={object.name}
        columnStart={object.col}
        columnEnd={object.col + object.colspan}
        rowStart={object.row}
        rowEnd={object.row + object.rowspan}
      />
    ));
  }

  render() {
    const {
      qlikAppId,
      qlikSheetId,
      pageName,
      downAndDistanceSelections,
      fieldZoneSelections,
      breadcrumbsSelections,
      gamesPrintSelections,
      team,
    } = this.props;
    const { isQlikDataLoaded } = this.state;
    if (!qlikAppId || !qlikSheetId) {
      return null;
    }
    const pageTitle = team ? `${team} - ${pageName}` : pageName;

    return (
      <QlikApp id="PrintArea">
        { isQlikDataLoaded ? (
          <React.Fragment>
            <PrintInformation className="print-only">
              <PageName>{pageTitle}</PageName>
              {gamesPrintSelections
                 && <SidebarSelections>{gamesPrintSelections}</SidebarSelections>}
              {downAndDistanceSelections && (
              <SidebarSelections>{downAndDistanceSelections}</SidebarSelections>
              )}
              {fieldZoneSelections
                 && <SidebarSelections>{fieldZoneSelections}</SidebarSelections>}
              {breadcrumbsSelections
                 && <SidebarSelections>{breadcrumbsSelections}</SidebarSelections>}
            </PrintInformation>
          </React.Fragment>
        ) : (
          <ApplicationSpinner />
        )
        }
        <QlikGridContainer>{this.renderQlikObjectContainers()}</QlikGridContainer>
      </QlikApp>
    );
  }
}

QlikGrid.propTypes = {
  qlikAppId: PropTypes.string.isRequired, // supplied from Redux
  qlikSheetId: PropTypes.string.isRequired, // supplied from Redux
  pageName: PropTypes.string.isRequired, // supplied from Redux
  objects: PropTypes.array.isRequired, // supplied from Redux
  setGridObjects: PropTypes.func.isRequired, // supplied from Redux
  ddSelections: PropTypes.object.isRequired,
  team: PropTypes.string.isRequired,
  fzSelections: PropTypes.object.isRequired,
  ddPossibleSelections: PropTypes.array.isRequired,
  fzPossibleSelections: PropTypes.array.isRequired,
  gamesPrintSelections: PropTypes.string.isRequired,
  downAndDistanceSelections: PropTypes.string.isRequired,
  fieldZoneSelections: PropTypes.string.isRequired,
  breadcrumbsSelections: PropTypes.string.isRequired,
};

const mapStateToProps = state => ({
  qlikAppId: getQlikAppId(state),
  qlikSheetId: getQlikSheetId(state),
  pageName: state.navigationSelection.page.navPageid,
  objects: state.grid.objects,
  team: state.teamSeasonGameSelection.team,
  ddSelections: state.football.downDistance.selections,
  ddPossibleSelections: state.football.downDistance.possibleSelections,
  fzSelections: state.football.fieldZone.selections,
  fzPossibleSelections: state.football.fieldZone.possibleSelections,
  downAndDistanceSelections: getPrintDownAndDistanceValues(state),
  fieldZoneSelections: getPrintFieldZoneValues(state),
  breadcrumbsSelections: getPrintBreadcrumbsValues(state),
  gamesPrintSelections: getPrintGameValues(state),
});

const mapDispatchToProps = {
  setGridObjects,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(QlikGrid);
